'use client';

import { formatReadableNumber } from '@lfg/core/lib';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperOptions } from 'swiper/types';

export interface FeaturedNodeItem {
  id: string;
  name: string;
  currentTier: number | string;
  price: number | string;
  token?: string;
  asset: string; // background image url
  nodeSaleUrl: string;
  createdBy: string;
}

interface BannerCarouselSwiperProps {
  items: FeaturedNodeItem[];
}

export const BannerCarouselSwiper = ({ items }: BannerCarouselSwiperProps) => {
  const swiperRef = useRef<SwiperType | null>(null);

  const swiperOptions: SwiperOptions = {
    modules: [Autoplay, EffectFade, Navigation, Pagination],
    spaceBetween: 0,
    centeredSlides: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true,
    },
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    navigation: false,
    pagination: {
      el: '.hero-pagination',
      clickable: true,
      bulletClass: 'hero-pagination-bullet',
      bulletActiveClass: 'hero-pagination-bullet-active',
    },
    loop: true,
    speed: 1000,
  };

  const handlePrev = () => {
    swiperRef.current?.slidePrev();
  };

  const handleNext = () => {
    swiperRef.current?.slideNext();
  };

  return (
    <Stack width={'100%'} alignItems={'center'} padding={{ xs: '0 12px 12px', md: '0 24px' }}>
      <Stack
        width={'100%'}
        height={{ xs: '480px', md: '600px' }}
        position={'relative'}
        sx={{
          borderRadius: '0',
          overflow: 'hidden',
          marginTop: { xs: '24px', md: '80px' },
          fontFamily: 'Poppins, sans-serif',
          '& *': {
            fontFamily: 'Poppins, sans-serif !important',
          },
        }}>
        {/* Navigation Buttons */}
        <IconButton
          onClick={handlePrev}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            left: { xs: '12px', md: '24px' },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            padding: { xs: '8px', md: '12px' },
            cursor: 'pointer',
            background: '#890096',
            border: 'none',
            borderRadius: '100px',
            color: '#FFFFFF',
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            transition: 'all 0.3s ease',
            '&:hover': {
              background: '#9F26AB',
              transform: 'translateY(-50%) translateX(-2px)',
              boxShadow: '0px 8px 25px rgba(137, 0, 150, 0.3)',
            },
            '&:active': {
              transform: 'translateY(-50%) translateX(-1px)',
            },
          }}>
          <ArrowBack sx={{ fontSize: { xs: '20px', md: '24px' } }} />
        </IconButton>

        <IconButton
          onClick={handleNext}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute',
            right: { xs: '12px', md: '24px' },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            padding: { xs: '8px', md: '12px' },
            cursor: 'pointer',
            background: '#890096',
            border: 'none',
            borderRadius: '100px',
            color: '#FFFFFF',
            width: { xs: 40, md: 48 },
            height: { xs: 40, md: 48 },
            transition: 'all 0.3s ease',
            '&:hover': {
              background: '#9F26AB',
              transform: 'translateY(-50%) translateX(2px)',
              boxShadow: '0px 8px 25px rgba(137, 0, 150, 0.3)',
            },
            '&:active': {
              transform: 'translateY(-50%) translateX(1px)',
            },
          }}>
          <ArrowForward sx={{ fontSize: { xs: '20px', md: '24px' } }} />
        </IconButton>

        <Swiper
          {...swiperOptions}
          className='hero-swiper'
          onSwiper={swiper => {
            swiperRef.current = swiper;
          }}>
          {items?.map((item, index) => (
            <SwiperSlide key={`${item.id}_${index}`}>
              <Stack
                width={'100%'}
                height={'100%'}
                position={'relative'}
                sx={{
                  background: `linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 100%), rgba(0, 0, 0, 0.3)`,
                  minHeight: { xs: '480px', md: '600px' },
                  borderRadius: '0 0 24px 24px',
                  overflow: 'hidden',
                }}>
                {/* Background Image */}
                <Stack
                  position={'absolute'}
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  zIndex={1}
                  sx={{
                    backgroundImage: `url(${item.asset})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  }}
                />

                <Stack
                  position={'relative'}
                  zIndex={2}
                  width={'100%'}
                  height={'100%'}
                  alignItems={'flex-end'}
                  justifyContent={'flex-end'}
                  padding={{ xs: '24px', md: '40px' }}
                  sx={{
                    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1) 100%)',
                  }}>
                  <Stack
                    width={'100%'}
                    maxWidth={{ xs: '100vw', md: '480px' }}
                    gap={{ xs: 3, md: 4 }}
                    position={'absolute'}
                    left={{ xs: '24px', md: '10%' }}
                    bottom={{ xs: '24px', md: '10%' }}
                    alignItems={'flex-start'}>
                    <Stack width={'100%'} gap={{ xs: 1, md: 1 }}>
                      <Stack
                        width={'fit-content'}
                        alignItems={'center'}
                        direction={'row'}
                        justifyContent={'center'}
                        gap={1}
                        padding={{ xs: '6px 14px', md: '6px 18px' }}
                        sx={{
                          background: 'rgba(137, 0, 150, 0.2)',
                          border: '1px solid #890096',
                          borderRadius: '100px',
                        }}>
                        <Stack
                          width={9.23}
                          height={9.24}
                          borderRadius={'50%'}
                          bgcolor={'#B64CC0'}
                          sx={{ boxShadow: '0px 0px 6px 0px rgba(255, 255, 255, 0.7)' }}
                        />
                        <Typography
                          color={'#FFFFFF'}
                          variant={'bodyMedium'}
                          fontWeight={'400'}
                          fontSize={{ xs: '12px', md: '14px' }}
                          lineHeight={{ xs: '16px', md: '20px' }}
                          sx={{ fontFamily: 'Poppins' }}>
                          Featured
                        </Typography>
                      </Stack>

                      <Typography
                        component={'h2'}
                        variant={'displayMedium'}
                        color={'#FFFFFF'}
                        fontSize={{ xs: '24px', md: '40px' }}
                        lineHeight={{ xs: '28px', md: '60px' }}
                        fontWeight={600}
                        sx={{ fontFamily: 'Poppins' }}>
                        {item.name}
                      </Typography>
                    </Stack>

                    <Stack
                      width={'100%'}
                      maxWidth={{ xs: '70vw', md: '90vw' }}
                      direction={'column'}
                      gap={{ xs: 0.5, md: 0.5 }}
                      padding={{ xs: '16px', md: '24px' }}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        borderRadius: '16px',
                        backdropFilter: 'blur(15px)',
                      }}>
                      {/* First Row: Current Tier | Price | Arrow Button */}
                      <Stack direction={'row'} alignItems={'center'} gap={{ xs: 2, md: 3 }} width={'100%'}>
                        <Stack gap={{ xs: 1, md: 1.5 }} flex={1}>
                          <Typography
                            color={'rgba(255, 255, 255, 0.6)'}
                            fontSize={{ xs: '14px', md: '16px' }}
                            fontWeight={400}
                            sx={{ fontFamily: 'Poppins' }}>
                            Current Tier
                          </Typography>
                          <Typography
                            color={'#FFFFFF'}
                            fontSize={{ xs: '18px', md: '24px' }}
                            fontWeight={600}
                            sx={{ fontFamily: 'Poppins' }}>
                            TIER {item.currentTier}
                          </Typography>
                        </Stack>

                        {/* Divider Line */}
                        <Box
                          width={'0px'}
                          height={'100%'}
                          sx={{
                            border: '1px solid rgba(255, 255, 255, 0.06)',
                            display: { xs: 'none', sm: 'block' },
                          }}
                        />

                        {/* Price Info */}
                        <Stack
                          gap={{ xs: 1, md: 1.5 }}
                          flex={1}
                          sx={{
                            borderLeft: { xs: '1px solid rgba(255,255,255,0.06)', md: 'none' },
                            paddingLeft: { xs: '16px', md: 0 },
                          }}>
                          <Typography
                            color={'rgba(255, 255, 255, 0.6)'}
                            fontSize={{ xs: '14px', md: '16px' }}
                            fontWeight={400}
                            sx={{ fontFamily: 'Poppins' }}>
                            Price
                          </Typography>
                          <Stack direction={'row'} alignItems={'center'} gap={1}>
                            <Typography
                              color={'#FFFFFF'}
                              fontSize={{ xs: '18px', md: '24px' }}
                              fontWeight={600}
                              sx={{ fontFamily: 'Poppins' }}>
                              {formatReadableNumber(Number(item.price) || 0, {
                                minimumFractionDigits: 2,
                                style: 'decimal',
                                preventRounding: true,
                              })}
                            </Typography>
                            <Typography
                              color={'#FFFFFF'}
                              fontSize={{ xs: '18px', md: '24px' }}
                              fontWeight={300}
                              sx={{ fontFamily: 'Poppins', opacity: 0.5 }}>
                              {(item.token || 'USDC').toUpperCase()}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* Pagination Indicator */}
                        <IconButton
                          size='small'
                          onClick={() => window.open(item.nodeSaleUrl, '_blank')}
                          sx={{
                            padding: { xs: '8px 12px', md: '12px 12px' },
                            cursor: 'pointer',
                            background: '#890096',
                            border: 'none',
                            borderRadius: '100px',
                            color: '#FFFFFF',
                            fontSize: '16px',
                            fontWeight: 600,
                            fontFamily: 'Poppins',
                            textTransform: 'none',
                            transition: 'all 0.3s ease',
                            width: { xs: 36, md: 48 },
                            height: { xs: 36, md: 48 },
                            '&:hover': {
                              background: '#9F26AB',
                              transform: 'translateY(-2px)',
                              boxShadow: '0px 8px 25px rgba(137, 0, 150, 0.3)',
                            },
                          }}>
                          <ArrowForward />
                        </IconButton>
                      </Stack>

                      {/* Second Row: Created By */}
                      {item.createdBy && (
                        <>
                          {/* Divider Line */}
                          <Box width={'100%'} height={'0px'} />
                          <Stack
                            gap={{ xs: 1, md: 1.5 }}
                            width={'100%'}
                            sx={{
                              userSelect: 'text',
                              WebkitUserSelect: 'text',
                              MozUserSelect: 'text',
                              msUserSelect: 'text',
                            }}>
                            <Typography
                              component={'span'}
                              color={'rgba(255, 255, 255, 0.6)'}
                              fontSize={{ xs: '10px', md: '12px' }}
                              fontWeight={400}
                              sx={{
                                fontFamily: 'Poppins',
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                MozUserSelect: 'text',
                                msUserSelect: 'text',
                                cursor: 'text',
                              }}>
                              Created by: {item.createdBy}
                            </Typography>
                          </Stack>
                        </>
                      )}
                    </Stack>

                    {/* Button Group */}
                    <Stack direction={'row'} alignItems={'center'} gap={2}>
                      <Box
                        component={'button'}
                        onClick={() => window.open(item.nodeSaleUrl, '_blank')}
                        sx={{
                          padding: { xs: '10px 24px', md: '12px 32px' },
                          background: '#890096',
                          border: 'none',
                          borderRadius: '100px',
                          color: '#FFFFFF',
                          fontSize: '16px',
                          fontWeight: 600,
                          fontFamily: 'Poppins',
                          textTransform: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: '#9F26AB',
                            transform: 'translateY(-2px)',
                            boxShadow: '0px 8px 25px rgba(137, 0, 150, 0.3)',
                          },
                        }}>
                        View Collection
                      </Box>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Styles for Swiper */}
        <style jsx global>{`
          .hero-swiper {
            width: 100%;
            height: 100%;
          }

          .hero-swiper .swiper-slide {
            width: 100%;
            height: 100%;
          }

          .hero-swiper .swiper-button-disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .hero-swiper * {
            font-family: 'Poppins', sans-serif !important;
          }
        `}</style>
      </Stack>

      <Box
        className='hero-pagination'
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          marginTop: '16px',
          '& .hero-pagination-bullet': {
            width: { xs: '40px', md: '56px' },
            height: '6px',
            borderRadius: '3px',
            background: 'rgba(255, 255, 255, 0.3)',
            opacity: 1,
            transition: 'all 0.3s ease',
            cursor: 'pointer',
          },
          '& .hero-pagination-bullet-active': {
            background: '#B64CC0',
            transform: 'scaleY(1.2)',
          },
        }}
      />
    </Stack>
  );
};
